import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Question, Tray } from "@phosphor-icons/react";
import {
  OrgSwitcher,
  OrgSwitcherTrigger,
  Sidebar,
  SidebarCollapseButton,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarItem,
  SidebarSearch,
} from "@hulahoop/ui";
import { usePendingSummary } from "@/application/queries";
import { useCurrentOrganizationId } from "@/application/workflows/organization";
import { ROUTES } from "@/app/routes";
import type { UserOrganization } from "@/domain/organization";
import { ShellCounter } from "./ShellCounter";
import { ShellOrganizationMark } from "./ShellOrganizationMark";
import { ShellOrganizationMenu } from "./ShellOrganizationMenu";
import { ShellSidebarRail } from "./ShellSidebarRail";
import { ShellQuickCreate } from "./ShellQuickCreate";
import {
  SHELL_NAVIGATION,
  SHELL_NAVIGATION_GROUPS,
  isShellNavigationActive,
} from "./shellNavigation";

interface ShellSidebarProps {
  organizationName: string;
  organizationLogoUrl?: string | null;
  organizations: UserOrganization[];
  selectedUuid: string | null;
  switching: boolean;
  inboxOpen: boolean;
  unreadCount: number;
  onSwitch: (uuid: string) => void;
  onSearch: () => void;
  onInbox: () => void;
  onHelp: () => void;
}

export function ShellSidebar({
  organizationName,
  organizationLogoUrl,
  organizations,
  selectedUuid,
  switching,
  inboxOpen,
  unreadCount,
  onSwitch,
  onSearch,
  onInbox,
  onHelp,
}: ShellSidebarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const orgId = useCurrentOrganizationId();
  const urgentCount = usePendingSummary(orgId).data?.urgent_items ?? 0;
  const organizationLabel = t("shell.organization", { name: organizationName });
  const menu = (
    <ShellOrganizationMenu
      name={organizationName}
      logoUrl={organizationLogoUrl}
      organizations={organizations}
      selectedUuid={selectedUuid}
      onSwitch={onSwitch}
    />
  );
  return (
    <>
      <ShellSidebarRail
        organizationName={organizationName}
        organizationLogoUrl={organizationLogoUrl}
        organizationLabel={organizationLabel}
        menu={menu}
        switching={switching}
        inboxOpen={inboxOpen}
        onSearch={onSearch}
        onInbox={onInbox}
        onHelp={onHelp}
      />

      <Sidebar className="border-r-0">
        <SidebarHeader className="gap-1.5 border-b-0 px-2 py-0 pb-1">
          <div className="flex min-h-(--control-h-xl) items-center gap-1">
            <OrgSwitcher>
              <OrgSwitcherTrigger
                logo={
                  <ShellOrganizationMark name={organizationName} logoUrl={organizationLogoUrl} />
                }
                disabled={switching}
              >
                {organizationName}
              </OrgSwitcherTrigger>
              {menu}
            </OrgSwitcher>
            <SidebarCollapseButton className="ml-auto" aria-label={t("nav.toggleSidebar")} />
          </div>
          <div className="flex items-center gap-1.5">
            <SidebarSearch variant="outline" className="min-w-0 flex-1" onClick={onSearch}>
              {t("shell.search_field")}
            </SidebarSearch>
            <ShellQuickCreate />
          </div>
        </SidebarHeader>
        <SidebarContent role="navigation" aria-label={t("shell.primary_navigation")}>
          {SHELL_NAVIGATION_GROUPS.map((group) => (
            <SidebarGroup key={group}>
              {group && <SidebarGroupLabel>{t(group)}</SidebarGroupLabel>}
              {SHELL_NAVIGATION.filter((item) => item.group === group).map(
                ({ path, label, icon: Icon }) => (
                  <SidebarItem
                    key={path}
                    render={<Link to={path} />}
                    icon={Icon}
                    badge={
                      path === ROUTES.TASKS && urgentCount > 0 ? (
                        <ShellCounter n={urgentCount} />
                      ) : undefined
                    }
                    active={isShellNavigationActive(path, pathname)}
                  >
                    {t(label)}
                  </SidebarItem>
                ),
              )}
              {group === "" && (
                <SidebarItem
                  render={<button type="button" className="text-left" />}
                  icon={Tray}
                  badge={unreadCount > 0 ? <ShellCounter n={unreadCount} /> : undefined}
                  aria-expanded={inboxOpen}
                  aria-current={false}
                  active={inboxOpen}
                  onClick={onInbox}
                >
                  {t("shell.inbox")}
                </SidebarItem>
              )}
            </SidebarGroup>
          ))}
        </SidebarContent>
        <SidebarFooter className="border-t-0">
          <SidebarItem
            render={<button type="button" className="text-left" />}
            icon={Question}
            aria-current={false}
            onClick={onHelp}
          >
            {t("shell.help")}
          </SidebarItem>
        </SidebarFooter>
      </Sidebar>
    </>
  );
}
